import { styled } from '@linaria/react';
import { Select } from '../../../../ui-library/select/select';
import type { ApprovalAggregate } from './agent-customize.type';

interface ApprovalGroupSelectProps {
  /** Current collective approval posture of the capability's write tools. */
  value: ApprovalAggregate;
  /** Fired with the chosen posture. `custom` is display-only and never emitted. */
  onChange: (value: Exclude<ApprovalAggregate, 'custom'>) => void;
  /** Stable selector hook for tests (e.g. `approval-select-targets`). */
  dataBf?: string;
}

const OPTION_ALLOW = { value: 'allow', label: 'Always allow' };
const OPTION_APPROVAL = { value: 'approval', label: 'Approval required' };
const OPTION_CUSTOM = { value: 'custom', label: 'Custom' };

/**
 * The per-capability approval control: a dropdown that governs whether every
 * write tool in the capability runs autonomously ("Allow") or requires human
 * approval ("Approval required"). "Custom" appears only when the underlying
 * tools are in a mixed state and is display-only (selecting it is a no-op).
 *
 * @param {ApprovalGroupSelectProps} props - Current posture, change handler, test hook
 * @returns {JSX.Element} The approval dropdown
 */
export function ApprovalGroupSelect({ value, onChange, dataBf }: ApprovalGroupSelectProps) {
  const options = value === 'custom' ? [OPTION_ALLOW, OPTION_APPROVAL, OPTION_CUSTOM] : [OPTION_ALLOW, OPTION_APPROVAL];

  return (
    <SelectWrap>
      <Select
        data-bf={dataBf}
        size="md"
        radius="md"
        value={value}
        options={options}
        onChange={(next) => {
          // "Custom" is a display-only aggregate; it cannot be applied.
          if (next === 'custom') return;
          onChange(next as Exclude<ApprovalAggregate, 'custom'>);
        }}
      />
    </SelectWrap>
  );
}

const SelectWrap = styled.div`
  width: 190px;
  flex-shrink: 0;
`;
